import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Chip, Stack, Alert, CircularProgress, Box, Typography } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface IcpDetailsModalProps {
  open: boolean;
  icpId: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

interface IcpRecord {
  id: string;
  title: string;
  data: string;
  default: boolean;
  createdAt?: string;
}

const IcpDetailsModal: React.FC<IcpDetailsModalProps> = ({ open, icpId, onClose, onSaved }) => {
  const [icp, setIcp] = useState<IcpRecord | null>(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditable = icp && !icp.default;

  useEffect(() => {
    const fetchIcp = async () => {
      if (!open || !icpId) return;
      setError(null);
      setLoading(true);
      try {
        // Reuse list endpoint and filter locally to avoid adding a new API for single ICP
        const resp = await axios.get(`${API_URL}/api/icps`, { withCredentials: true });
        const found = (resp.data.icps || []).find((i: any) => i.id === icpId) || null;
        if (!found) {
          setError('Persona not found');
          setIcp(null);
        } else {
          setIcp(found);
          setTitle(found.title || '');
          setData(found.data || '');
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load Persona');
      } finally {
        setLoading(false);
      }
    };
    fetchIcp();
  }, [open, icpId]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (!icp || icp.default) return;
    const trimmedTitle = title.trim();
    const trimmedData = data.trim();
    if (!trimmedTitle || !trimmedData) {
      setError('Title and Summary are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${API_URL}/api/icps/${icp.id}`, { title: trimmedTitle, data: trimmedData }, { withCredentials: true });
      if (onSaved) onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save Persona');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Persona Details</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {icp && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={icp.default ? 'Default' : 'Custom'} color={icp.default ? 'default' : 'primary'} variant={icp.default ? 'outlined' : 'filled'} />
            </Stack>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required disabled={!isEditable || saving} />
            <TextField label="Summary" value={data} onChange={(e) => setData(e.target.value)} fullWidth required multiline minRows={6} disabled={!isEditable || saving} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Close</Button>
        {!icp?.default && (
          <Box sx={{ position: 'relative' }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
            {saving && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-12px', ml: '-12px' }} />}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IcpDetailsModal;


